/**
 * Copyright (C) 2021 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import { ClassNameMap } from '@mui/styles/';
import appTheme from '../../common/theme/appTheme.json';
import { useFormikContext } from 'formik';
import { FormikFieldEnum, FormValues, useAutopopulateFormikValue } from './MissionDetailsForm';
import { shallowEqual, useSelector } from 'react-redux';
import { selectMissionPlanById } from '../../../store';

const KUKARRAY_2 = 'KuKarray 2.0';
export interface HcxButtonsProps {
  styling: ClassNameMap<'label' | 'input' | 'error'>;
}

const buttonContainer = {
  backgroundColor: appTheme.mptGray,
  borderRadius: '18px',
  width: '100%',
};
const buttonSelected = {
  backgroundColor: appTheme.mptLightBlue,
  color: appTheme.darkGrayTextColor,

  '&:hover': { backgroundColor: appTheme.mptLightBlue },
};
const buttonUnselected = {
  backgroundColor: appTheme.mptGray,
  color: appTheme.grayTextColor,
  '&:hover': { backgroundColor: appTheme.mptGray },
};
const buttonCommonStyle = {
  width: '50%',
  borderRadius: '18px',
  fontWeight: 400,
  fontSize: '14px',
};
const HcxButtons = (props: HcxButtonsProps) => {
  const { styling } = props;
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const missionPlanToEdit = useSelector(selectMissionPlanById, shallowEqual);

  // Disables buttons when the selected antenna is not KuKarray 2.0
  useEffect(() => {
    setIsDisabled(values.antennaType.includes(KUKARRAY_2) ? false : true);
  }, [values.antennaType]);

  // Highlights 'Disabled' button when buttons are disabled
  useEffect(() => {
    if (isDisabled) setFieldValue(FormikFieldEnum.HCX, false, false);
  }, [isDisabled, setFieldValue]);

  //  Populates hcx field when editting a plan
  useAutopopulateFormikValue(FormikFieldEnum.HCX, missionPlanToEdit.useHcxBeams);

  const hcxTrue = () => {
    setFieldValue(FormikFieldEnum.HCX, true, false);
  };

  const hcxFalse = () => {
    setFieldValue(FormikFieldEnum.HCX, false, false);
  };

  return (
    <Box flexBasis="48%">
      <label htmlFor={FormikFieldEnum.HCX} className={styling.label}>
        HCX Service
      </label>
      <Box sx={{ ...buttonContainer }}>
        <Button
          color="primary"
          sx={{ ...(!values.hcx && buttonSelected), ...(values.hcx && buttonUnselected), ...buttonCommonStyle }}
          onClick={() => hcxFalse()}
          disabled={isDisabled}
        >
          Disabled
        </Button>
        <Button
          color="primary"
          sx={{ ...(values.hcx && buttonSelected), ...(!values.hcx && buttonUnselected), ...buttonCommonStyle }}
          onClick={() => hcxTrue()}
          disabled={isDisabled}
        >
          Enabled
        </Button>
      </Box>
    </Box>
  );
};

export default HcxButtons;
